import { useEffect, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "../StoreMap.css";
import { MapPin } from "lucide-react";

// Fix for default markers in React Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

// Helper to create custom icons
const createIcon = (isSelected: boolean, isDisabled: boolean, color: string) => {
  const bgColor = isSelected ? "#9333ea" : isDisabled ? "#9ca3af" : color;
  const opacity = isDisabled ? 0.5 : 1;
  
  return L.divIcon({
    html: `
      <div style="
        background-color: ${bgColor}; 
        width: 30px;
        height: 30px;
        border-radius: 50%;
        border: 3px solid white;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: bold;
        color: white;
        font-size: 12px;
        transition: background-color 0.2s;
        opacity: ${opacity};
      ">
        ${isSelected ? '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>' : ""}
      </div>
    `,
    className: `custom-store-icon ${isSelected ? "selected" : ""} ${isDisabled ? "disabled" : ""}`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
    popupAnchor: [0, -15],
  });
};

interface MapStoreSelectorProps {
  stores: any[]; // Using any to handle both store types flexibly
  selectedStoreIds: string[];
  onStoreSelect: (storeId: string) => void;
  retailer: "walmart" | "home_depot";
  center?: { lat: number; long: number };
  zoom?: number;
  height?: string;
  maxStores: number;
}

// Component to handle map bounds updates
function MapBoundsHandler({ stores, center }: { stores: any[], center?: { lat: number; long: number } }) {
  const map = useMap();

  useEffect(() => {
    // Invalidate size to ensure map renders correctly
    setTimeout(() => {
      map.invalidateSize();
    }, 100);

    if (stores.length > 0) {
      const validStores = stores.filter((s) => {
        const lat = s.geo?.lat || parseFloat(s.latitude || "0");
        const lng = s.geo?.long || parseFloat(s.longitude || "0");
        return lat && lng && lat !== 0 && lng !== 0;
      });

      if (validStores.length > 0) {
        const bounds = L.latLngBounds(
          validStores.map((s) => [
            s.geo?.lat || parseFloat(s.latitude || "0"),
            s.geo?.long || parseFloat(s.longitude || "0"),
          ])
        );
        // Add center to bounds if available
        if (center) {
          bounds.extend([center.lat, center.long]);
        }
        map.fitBounds(bounds, { padding: [50, 50] });
      }
    } else if (center) {
      map.setView([center.lat, center.long], 10);
    }
  }, [stores, center, map]);

  return null;
}

export default function MapStoreSelector({
  stores,
  selectedStoreIds,
  onStoreSelect,
  retailer,
  center,
  zoom = 10,
  height = "400px",
  maxStores,
}: MapStoreSelectorProps) {
  const defaultCenter: [number, number] = center
    ? [center.lat, center.long]
    : [39.8283, -98.5795];

  const markerColor = retailer === "walmart" ? "#0071ce" : "#f96302";
  const isAtLimit = selectedStoreIds.length >= maxStores;

  // Create icons
  const unselectedIcon = useMemo(() => createIcon(false, false, markerColor), [markerColor]);
  const selectedIcon = useMemo(() => createIcon(true, false, markerColor), [markerColor]);
  const disabledIcon = useMemo(() => createIcon(false, true, markerColor), [markerColor]);

  // Convert height to pixels if it's a percentage
  const mapHeight = height === "100%" ? "500px" : height;

  return (
    <div 
      className="relative w-full rounded-xl overflow-hidden border border-gray-200 shadow-sm"
      style={{ height: mapHeight, minHeight: "400px" }}
    >
      <MapContainer
        key={`map-${stores.length}-${center?.lat || 0}`}
        center={defaultCenter}
        zoom={zoom}
        style={{ height: "100%", width: "100%", zIndex: 0 }}
        scrollWheelZoom={true}
        zoomControl={true}
        attributionControl={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          maxZoom={19}
        />
        
        <MapBoundsHandler stores={stores} center={center} />

        {stores.map((store) => {
          const isSelected = selectedStoreIds.includes(store.storeId || store.id);
          const isDisabled = !isSelected && isAtLimit;
          const lat = store.geo?.lat ?? (store.latitude ? parseFloat(String(store.latitude)) : null);
          const lng = store.geo?.long ?? (store.longitude ? parseFloat(String(store.longitude)) : null);
          
          if (!lat || !lng || isNaN(lat) || isNaN(lng) || lat === 0 || lng === 0) return null;

          const icon = isSelected ? selectedIcon : isDisabled ? disabledIcon : unselectedIcon;

          return (
            <Marker
              key={store.storeId || store.id}
              position={[lat, lng]}
              icon={icon}
              eventHandlers={{
                click: () => {
                  if (!isDisabled) {
                    onStoreSelect(store.storeId || store.id);
                  }
                },
              }}
            >
              <Popup>
                <div className="p-1 min-w-[200px]">
                  <h3 className={`font-semibold text-sm mb-1 ${isDisabled ? "text-gray-400" : ""}`}>
                    {retailer === "walmart" ? "Walmart" : store.store_title || "HD"}
                  </h3>
                  <p className={`text-xs mb-2 ${isDisabled ? "text-gray-400" : "text-gray-600"}`}>
                    {store.address || store.address_1}
                    <br />
                    {store.city}, {store.state} {store.postal_code || store.zip}
                  </p>
                  {isDisabled && (
                    <p className="text-xs text-gray-500 mb-2 italic">
                      Store limit reached ({maxStores}/{maxStores})
                    </p>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isDisabled) {
                        onStoreSelect(store.storeId || store.id);
                      }
                    }}
                    disabled={isDisabled}
                    className={`w-full py-1.5 px-3 rounded text-xs font-medium transition-colors ${
                      isDisabled
                        ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                        : isSelected
                        ? "bg-red-50 text-red-600 hover:bg-red-100"
                        : "bg-purple-600 text-white hover:bg-purple-700"
                    }`}
                  >
                    {isSelected ? "Remove Store" : "Select Store"}
                  </button>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
      
      {/* Legend/Overlay */}
      {stores.length > 0 && (
        <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm p-3 rounded-lg shadow-md border border-gray-200 text-xs z-[400]">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-3 h-3 rounded-full bg-purple-600 border border-white shadow-sm"></div>
            <span className="text-gray-700 font-medium">Selected</span>
          </div>
          {isAtLimit && (
            <div className="flex items-center gap-2 mb-2">
              <div className="w-3 h-3 rounded-full bg-gray-400 border border-white shadow-sm opacity-50"></div>
              <span className="text-gray-500 font-medium">Unavailable</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <div 
              className="w-3 h-3 rounded-full border border-white shadow-sm"
              style={{ backgroundColor: markerColor }}
            ></div>
            <span className="text-gray-700 font-medium">Available</span>
          </div>
        </div>
      )}

      {/* Empty State */}
      {stores.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-50/80 backdrop-blur-sm z-[100]">
          <div className="text-center">
            <MapPin className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-600">No stores to display</p>
          </div>
        </div>
      )}
    </div>
  );
}

